import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, User, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";
import { API } from "../App";

export default function ChatButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! 👋 I'm BQL's Query Bot. I can help you with:" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasShownAutoGreeting, setHasShownAutoGreeting] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-greeting when chat opens
  useEffect(() => {
    if (isOpen && !hasShownAutoGreeting) {
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, { 
          from: "bot", 
          text: "🏠 Looking for a professional? Post your job FREE!\n\n💼 Are you a professional? Subscribe to access quality leads!\n\nWhat can I help you with today?" 
        }]);
        setHasShownAutoGreeting(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isOpen, hasShownAutoGreeting]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageOverride = null) => {
    // Ensure messageOverride is a string, not an event object
    const messageToSend = (typeof messageOverride === 'string' ? messageOverride : null) || input.trim();
    if (!messageToSend || isLoading) return;
    
    setMessages(prev => [...prev, { from: "user", text: messageToSend }]);
    setInput("");
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageToSend,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { from: "bot", text: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        from: "bot", 
        text: "Sorry, I'm having trouble connecting. Please email bestqualityleads@bqleadsprogigs.com for help!" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (query) => {
    if (typeof query === 'string') {
      handleSend(query);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-all hover:scale-110"
        data-testid="chat-button"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-gray-200" data-testid="chat-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">BQL Query Bot</h3>
                <p className="text-xs text-blue-100">Auto-answers • 88 categories • 24/7</p>
              </div>
            </div>
          </div>

          {/* Quick Action Buttons */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex flex-wrap gap-2">
              {[
                { label: "💰 Pricing", query: "What are the subscription prices?" },
                { label: "📋 Categories", query: "What service categories do you have?" },
                { label: "🏠 Post a Job", query: "How do I post a job as a customer?" },
                { label: "💼 For Pros", query: "How does it work for professionals?" }
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => handleQuickAction(btn.query)}
                  className="text-xs px-3 py-1.5 bg-white border border-blue-200 rounded-full text-blue-700 hover:bg-blue-100 transition-colors"
                  disabled={isLoading}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={msg.id || `msg-${idx}-${msg.timestamp || Date.now()}`}
                className={`flex items-start gap-2 ${msg.from === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.from === "user" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
                }`}>
                  {msg.from === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div
                  className={`max-w-[75%] p-3 rounded-2xl text-sm ${
                    msg.from === "user"
                      ? "bg-blue-600 text-white rounded-tr-md"
                      : "bg-white text-gray-800 border border-gray-200 rounded-tl-md shadow-sm"
                  }`}
                >
                  <span className="whitespace-pre-line">{msg.text}</span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-200 text-gray-600">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-tl-md p-3 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              className="flex-1"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button
              onClick={() => handleSend()}
              size="icon"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={isLoading || !input.trim()}
              data-testid="chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
